/**
 * Elite Sales Workflow Templates — Production-grade prebuilt workflows for sales teams.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after trigger MUST extract named variables
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}

function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE SALES WORKFLOWS
// =============================================================================

export const ELITE_SALES_WORKFLOWS: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1. COLD OUTREACH SEQUENCER
  // ===================================================================

  "Elite Cold Outreach Sequencer": {
    nodes: [
      trigger("CRM Webhook: New lead for outreach", "webhook_app", {
        source: "Salesforce / HubSpot",
        event: "lead.created",
        howItStarts:
          "A new lead is added to your CRM or submitted manually for cold outreach",
        setup:
          "In Salesforce -> Setup -> Outbound Messages or HubSpot -> Workflows -> Webhook action, point to your workflow URL. Alternatively, POST lead data via API with name, company, and domain.",
        inputFields: [],
      }),

      // 2. Firmographic enrichment via Apollo
      n("2", "hitApi", "Enrich via Apollo", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_key}" },
            { key: "domain", value: "{lead_domain}" },
            { key: "first_name", value: "{lead_first_name}" },
            { key: "last_name", value: "{lead_last_name}" },
          ],
          variables: [
            { name: "person.title", customName: "job_title", type: "string" },
            {
              name: "person.email",
              customName: "verified_email",
              type: "string",
            },
            {
              name: "person.organization.name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "person.organization.estimated_num_employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "person.organization.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "person.organization.annual_revenue_printed",
              customName: "annual_revenue",
              type: "string",
            },
            {
              name: "person.linkedin_url",
              customName: "linkedin_url",
              type: "string",
            },
          ],
        },
      }),

      // 3. Contact discovery via Hunter.io with fallback
      n("3", "hitApi", "Verify email via Hunter.io", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-verifier?email={verified_email}&api_key={hunter_key}",
          headers: [],
          variables: [
            {
              name: "data.result",
              customName: "email_verification_result",
              type: "string",
            },
            {
              name: "data.score",
              customName: "email_deliverability_score",
              type: "number",
            },
            {
              name: "data.smtp_check",
              customName: "smtp_valid",
              type: "boolean",
            },
          ],
        },
      }),

      // 4. Email deliverability + compliance gate
      n("4", "condition", "Email deliverable? (score > 70)", 760, {
        conditionData: [
          {
            variable: "email_deliverability_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI research + personalized message package
      aiNode(
        "5",
        "AI research + personalize outreach",
        1000,
        "You are an elite SDR researcher. Research this prospect and write a hyper-personalized cold email. Reference their company size, industry, recent activity, and role. Keep under 120 words. Be specific, not generic. Respond JSON: {subject_line: string, email_body: string, personalization_hook: string, confidence_score: number, is_enterprise: string}",
        "Prospect: {lead_first_name} {lead_last_name}, Title: {job_title}, Company: {company_name}, Industry: {industry}, Employees: {employee_count}, Revenue: {annual_revenue}, LinkedIn: {linkedin_url}",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          { field: "confidence_score", as: "ai_confidence", type: "number" },
          { field: "is_enterprise", as: "is_enterprise", type: "string" },
        ],
      ),

      // 6. Rep approval gate for enterprise or low-confidence
      n("6", "approvalGate", "Rep approval (enterprise/low-confidence)", 1240, {
        approvalGateData: {
          message:
            "Review outreach to {lead_first_name} {lead_last_name} at {company_name} ({employee_count} employees).\n\nSubject: {subject_line}\nHook: {personalization_hook}\nConfidence: {ai_confidence}%\n\nApprove to send, reject to skip.",
        },
      }),

      // 7. Send via SendGrid with throttling
      n("7", "hitApi", "Send email via SendGrid", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{verified_email}","name":"{lead_first_name} {lead_last_name}"}],"subject":"{subject_line}"}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{sender_name}"}',
            },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{email_body}"}]',
            },
            {
              key: "mail_settings",
              value: '{"sandbox_mode":{"enable":false}}',
            },
            {
              key: "tracking_settings",
              value:
                '{"open_tracking":{"enable":true},"click_tracking":{"enable":true}}',
            },
          ],
          variables: [
            { name: "statusCode", customName: "send_status", type: "number" },
          ],
        },
      }),

      // 8. Engagement branching + CRM writeback
      n("8", "hitApi", "CRM writeback: outreach sent", 1720, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"last_outreach_date":"{current_date}","outreach_status":"sent","outreach_subject":"{subject_line}","personalization_hook":"{personalization_hook}"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_update_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // deliverable -> personalize
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Elite Cold Outreach Sequencer** — Full-stack cold outreach with enrichment, verification, AI personalization, and CRM sync.\n\n1. Lead enters via CRM webhook or manual submission\n2. **Apollo enrichment** pulls firmographics: title, company size, revenue, industry, LinkedIn\n3. **Hunter.io verifies** email deliverability with SMTP check\n4. Compliance gate: only proceeds if deliverability score > 70\n5. **AI writes hyper-personalized email** referencing prospect's role, company, and industry\n6. **Human approval gate** for enterprise accounts or low-confidence drafts\n7. **SendGrid sends** with open/click tracking enabled\n8. **CRM writeback** logs outreach date, subject, and personalization hook\n\n**To activate:** Say "connect apollo", "connect hunter", "connect openai", "connect sendgrid", "connect hubspot"`,
  },

  // ===================================================================
  // 2. LEAD QUALIFICATION & ROUTING
  // ===================================================================

  "Elite Lead Qualification & Routing": {
    nodes: [
      trigger("Inbound lead: Form or CRM webhook", "webhook_form", {
        source: "Outgrow Forms / HubSpot / Salesforce",
        event: "lead.created",
        howItStarts: "An inbound lead submits a form or is created in your CRM",
        setup:
          "Connect your lead capture form (Outgrow Forms, HubSpot Forms, website) or CRM lead trigger webhook to your workflow URL. The AI will extract lead details regardless of field naming.",
        inputFields: [],
      }),

      // 2. Dedupe + account match via CRM search
      n("2", "hitApi", "Dedupe + account match via HubSpot", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{lead_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","company","lifecyclestage","hubspot_owner_id"]',
            },
          ],
          variables: [
            {
              name: "total",
              customName: "existing_contact_count",
              type: "number",
            },
            {
              name: "results[0].id",
              customName: "existing_contact_id",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "existing_stage",
              type: "string",
            },
          ],
        },
      }),

      // 3. Enrichment + intent via Clearbit
      n("3", "hitApi", "Enrich + intent signals via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={lead_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            {
              name: "person.employment.title",
              customName: "job_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            { name: "company.tech", customName: "tech_stack", type: "string" },
          ],
        },
      }),

      // 4. AI scoring (fit, intent, authority, timing)
      aiNode(
        "4",
        "AI lead scoring: fit + intent + authority",
        760,
        "You are a lead scoring engine. Score this lead on four dimensions (each 0-25, total 0-100): FIT (company size, industry, revenue match), INTENT (form source, page visited, urgency signals), AUTHORITY (seniority, title, decision-maker likelihood), TIMING (budget cycle, hiring signals). Respond JSON: {total_score: number, fit_score: number, intent_score: number, authority_score: number, timing_score: number, reasoning: string, recommended_action: string, segment: string}",
        "Name: {lead_name}, Email: {lead_email}, Title: {job_title}, Seniority: {seniority}, Company: {company_name}, Employees: {employee_count}, Revenue: {annual_revenue}, Industry: {industry}, Tech: {tech_stack}, Existing contact: {existing_contact_count}, Existing stage: {existing_stage}",
        [
          { field: "total_score", as: "lead_score", type: "number" },
          { field: "fit_score", as: "fit_score", type: "number" },
          { field: "intent_score", as: "intent_score", type: "number" },
          { field: "authority_score", as: "authority_score", type: "number" },
          { field: "timing_score", as: "timing_score", type: "number" },
          { field: "reasoning", as: "score_reasoning", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "segment", as: "lead_segment", type: "string" },
        ],
      ),

      // 5. Policy + territory routing (condition: score > 80?)
      n("5", "condition", "Hot lead? (score > 80)", 1000, {
        conditionData: [
          {
            variable: "lead_score",
            value: "80",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Hot -> Assign to AE + Slack alert
      slackNode(
        "6",
        "Hot lead: Assign AE + Slack alert",
        1240,
        "🔥 *HOT LEAD — Immediate action required*\n\n*{lead_name}* ({lead_email})\n{job_title} at {company_name} ({employee_count} employees)\nScore: {lead_score}/100 (Fit:{fit_score} Intent:{intent_score} Auth:{authority_score} Time:{timing_score})\nSegment: {lead_segment}\n\nAction: {recommended_action}\nReasoning: {score_reasoning}",
      ),

      // 7. Warm -> Nurture sequence enrollment
      emailNode(
        "7",
        "Warm lead: Enroll in nurture sequence",
        1240,
        "{lead_email}",
        "Great to connect, {lead_name}!",
        "<p>Hi {lead_name},</p><p>Thanks for your interest. Based on what we know about {company_name}, here are some resources that teams in {industry} find valuable:</p><p><ul><li>Industry guide for {industry}</li><li>Case study: companies with {employee_count}+ employees</li><li>ROI calculator</li></ul></p><p>Would any of these be helpful? Happy to share more.</p>",
        460,
      ),

      // 8. Feedback loop: CRM writeback with score components
      n("8", "hitApi", "CRM writeback: score + routing", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{existing_contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"lead_score":"{lead_score}","fit_score":"{fit_score}","intent_score":"{intent_score}","authority_score":"{authority_score}","timing_score":"{timing_score}","lead_segment":"{lead_segment}","score_reasoning":"{score_reasoning}","recommended_action":"{recommended_action}","lifecyclestage":"marketingqualifiedlead"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_writeback_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // hot -> AE + Slack
      e("5", "7", "output_2"), // warm -> nurture
      e("6", "8"),
      e("7", "8"),
    ],
    summary: `**Elite Lead Qualification & Routing** — AI-powered four-dimension scoring with automatic hot/warm routing.\n\n1. Inbound lead arrives via form or CRM webhook\n2. **HubSpot dedupe** checks for existing contacts and lifecycle stage\n3. **Clearbit enrichment** pulls firmographics, seniority, tech stack\n4. **AI scores on 4 dimensions** (fit, intent, authority, timing) — each 0-25, total 0-100\n5. Score > 80 → **Hot path**: Slack alert to AE with full breakdown\n6. Score <= 80 → **Warm path**: nurture email with industry-relevant resources\n7. **CRM writeback** logs all score components, segment, and recommended action\n\n**To activate:** Say "connect hubspot", "connect clearbit", "connect openai", "connect slack", "connect email"`,
  },

  // ===================================================================
  // 3. LEAD ENRICH SCORE ASSIGN
  // ===================================================================

  "Elite Lead Enrich Score Assign": {
    nodes: [
      trigger("CRM: Contact record changed", "webhook_app", {
        source: "Salesforce / HubSpot",
        event: "contact.propertyChange",
        howItStarts:
          "A contact record is created or updated in your CRM, triggering re-enrichment and scoring",
        setup:
          "In HubSpot -> Workflows -> Trigger: Contact property change, or Salesforce -> Process Builder -> Contact update trigger. Point webhook to your workflow URL.",
        inputFields: [],
      }),

      // 2. Multi-source enrichment (Apollo + Clearbit)
      n("2", "hitApi", "Multi-source enrich: Apollo", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_key}" },
            { key: "email", value: "{contact_email}" },
          ],
          variables: [
            {
              name: "person.title",
              customName: "apollo_title",
              type: "string",
            },
            {
              name: "person.organization.name",
              customName: "apollo_company",
              type: "string",
            },
            {
              name: "person.organization.estimated_num_employees",
              customName: "apollo_employees",
              type: "number",
            },
            {
              name: "person.organization.industry",
              customName: "apollo_industry",
              type: "string",
            },
            {
              name: "person.organization.annual_revenue_printed",
              customName: "apollo_revenue",
              type: "string",
            },
            {
              name: "person.organization.founded_year",
              customName: "apollo_founded_year",
              type: "number",
            },
            {
              name: "person.seniority",
              customName: "apollo_seniority",
              type: "string",
            },
          ],
        },
      }),

      // 3. Conflict resolution via code node
      n("3", "runCode", "Resolve enrichment conflicts", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Merge Apollo + CRM data with conflict resolution
// Priority: Apollo for firmographics, CRM for relationship data
const result = {
  final_title: vars.apollo_title || vars.crm_title || "Unknown",
  final_company: vars.apollo_company || vars.crm_company || "Unknown",
  final_employees: vars.apollo_employees || vars.crm_employees || 0,
  final_industry: vars.apollo_industry || vars.crm_industry || "Unknown",
  final_revenue: vars.apollo_revenue || vars.crm_revenue || "Unknown",
  final_seniority: vars.apollo_seniority || "Unknown",
  data_completeness: 0,
  conflicts_found: []
};
// Calculate data completeness percentage
const fields = [result.final_title, result.final_company, result.final_industry, result.final_revenue, result.final_seniority];
result.data_completeness = Math.round((fields.filter(f => f !== "Unknown").length / fields.length) * 100);
// Detect conflicts
if (vars.apollo_company && vars.crm_company && vars.apollo_company !== vars.crm_company) {
  result.conflicts_found.push("company_name");
}
return result;`,
          outputVariables: [
            { name: "final_title", type: "string" },
            { name: "final_company", type: "string" },
            { name: "final_employees", type: "number" },
            { name: "final_industry", type: "string" },
            { name: "final_revenue", type: "string" },
            { name: "final_seniority", type: "string" },
            { name: "data_completeness", type: "number" },
          ],
        },
      }),

      // 4. Signal extraction (hiring, funding, tech changes)
      aiNode(
        "4",
        "AI extract buying signals",
        760,
        "Analyze this company profile for buying signals. Look for: recent hiring (growing team = budget), funding rounds, technology changes, leadership changes, expansion signals. Respond JSON: {hiring_signal: string, funding_signal: string, tech_change_signal: string, expansion_signal: string, signal_strength: string, signal_summary: string}",
        "Company: {final_company}, Industry: {final_industry}, Employees: {final_employees}, Revenue: {final_revenue}, Founded: {apollo_founded_year}, Seniority: {final_seniority}, Title: {final_title}",
        [
          { field: "hiring_signal", as: "hiring_signal", type: "string" },
          { field: "funding_signal", as: "funding_signal", type: "string" },
          {
            field: "tech_change_signal",
            as: "tech_change_signal",
            type: "string",
          },
          { field: "expansion_signal", as: "expansion_signal", type: "string" },
          { field: "signal_strength", as: "signal_strength", type: "string" },
          { field: "signal_summary", as: "signal_summary", type: "string" },
        ],
      ),

      // 5. ICP scoring via AI
      aiNode(
        "5",
        "AI ICP scoring",
        1000,
        "Score this contact against our Ideal Customer Profile. Consider: company size (50-5000 employees ideal), seniority (VP+ is best), industry fit, revenue, buying signals, data completeness. Respond JSON: {icp_score: number, icp_tier: string, territory: string, recommended_owner_role: string, priority: string, scoring_rationale: string}",
        "Title: {final_title}, Company: {final_company}, Employees: {final_employees}, Industry: {final_industry}, Revenue: {final_revenue}, Seniority: {final_seniority}, Data Completeness: {data_completeness}%, Signal Strength: {signal_strength}, Signals: {signal_summary}",
        [
          { field: "icp_score", as: "icp_score", type: "number" },
          { field: "icp_tier", as: "icp_tier", type: "string" },
          { field: "territory", as: "territory", type: "string" },
          {
            field: "recommended_owner_role",
            as: "recommended_owner_role",
            type: "string",
          },
          { field: "priority", as: "priority", type: "string" },
          {
            field: "scoring_rationale",
            as: "scoring_rationale",
            type: "string",
          },
        ],
      ),

      // 6. Segment + territory routing (condition)
      n("6", "condition", "Tier A prospect? (ICP score > 75)", 1240, {
        conditionData: [
          {
            variable: "icp_score",
            value: "75",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Owner brief generation
      aiNode(
        "7",
        "AI generate owner brief",
        1480,
        "Write a concise account owner briefing for this high-priority prospect. Include: who they are, why they matter, what signals suggest readiness, recommended first touch approach. Keep under 200 words. Respond JSON: {owner_brief: string, suggested_first_touch: string, talking_points: string}",
        "Contact: {contact_first_name} {contact_last_name}, Title: {final_title}, Company: {final_company} ({final_employees} emp, {final_industry}), ICP Score: {icp_score}, Tier: {icp_tier}, Territory: {territory}, Signals: {signal_summary}, Priority: {priority}",
        [
          { field: "owner_brief", as: "owner_brief", type: "string" },
          {
            field: "suggested_first_touch",
            as: "suggested_first_touch",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
        ],
      ),

      // 8. CRM writeback + publish
      n("8", "hitApi", "CRM writeback: enriched + scored", 1720, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"jobtitle":"{final_title}","company":"{final_company}","industry":"{final_industry}","icp_score":"{icp_score}","icp_tier":"{icp_tier}","territory":"{territory}","signal_strength":"{signal_strength}","signal_summary":"{signal_summary}","owner_brief":"{owner_brief}","data_completeness":"{data_completeness}","priority":"{priority}"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_updated_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Tier A -> owner brief
      e("6", "8", "output_2"), // Tier B/C -> CRM writeback only
      e("7", "8"),
    ],
    summary: `**Elite Lead Enrich Score Assign** — Multi-source enrichment with conflict resolution, buying signal detection, and ICP scoring.\n\n1. Contact change triggers re-enrichment pipeline\n2. **Apollo enrichment** pulls firmographics, seniority, revenue, industry\n3. **Code node** resolves conflicts between Apollo and CRM data, calculates completeness\n4. **AI extracts buying signals** — hiring, funding, tech changes, expansion\n5. **AI ICP scoring** rates fit across company size, seniority, industry, signals\n6. Tier A (score > 75) → **AI generates owner brief** with talking points and first-touch strategy\n7. All contacts → **CRM writeback** with enriched data, scores, signals, and briefs\n\n**To activate:** Say "connect apollo", "connect openai", "connect hubspot"`,
  },

  // ===================================================================
  // 4. AUTOMATED SALES FOLLOW-UP
  // ===================================================================

  "Elite Automated Sales Follow-Up": {
    nodes: [
      trigger("CRM: Deal stage changed", "webhook_app", {
        source: "Salesforce / HubSpot",
        event: "deal.propertyChange",
        howItStarts:
          "A deal moves to a new stage in your pipeline (e.g., demo completed, proposal sent, negotiation)",
        setup:
          "In HubSpot -> Workflows -> Deal-based -> Trigger: Deal stage changes, or Salesforce -> Process Builder -> Opportunity Stage change. Point webhook to your workflow URL.",
        inputFields: [],
      }),

      // 2. Context assembly from CRM
      n("2", "hitApi", "Assemble deal context from CRM", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?associations=contacts,companies&properties=dealname,amount,dealstage,pipeline,closedate,hubspot_owner_id,notes_last_updated",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            {
              name: "properties.dealname",
              customName: "deal_name",
              type: "string",
            },
            {
              name: "properties.amount",
              customName: "deal_amount",
              type: "string",
            },
            {
              name: "properties.dealstage",
              customName: "deal_stage",
              type: "string",
            },
            {
              name: "properties.closedate",
              customName: "close_date",
              type: "string",
            },
            {
              name: "properties.pipeline",
              customName: "pipeline_name",
              type: "string",
            },
            {
              name: "associations.contacts.results[0].id",
              customName: "primary_contact_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI stage-specific draft generation
      aiNode(
        "3",
        "AI draft stage-specific follow-up",
        520,
        "You are a sales follow-up specialist. Based on the deal stage, write the appropriate follow-up email. Stage-specific rules: DEMO_COMPLETED -> summarize value shown, propose next steps. PROPOSAL_SENT -> reinforce ROI, address common objections. NEGOTIATION -> create urgency, offer flexibility. VERBAL_COMMIT -> confirm timeline, outline onboarding. Keep under 150 words, professional but warm. Respond JSON: {subject: string, email_body: string, cta: string, risk_level: string, tone_appropriate: string, follow_up_timing_days: number}",
        "Deal: {deal_name}, Amount: ${deal_amount}, Stage: {deal_stage}, Close Date: {close_date}, Contact: {contact_first_name} {contact_last_name}, Company: {company_name}",
        [
          { field: "subject", as: "followup_subject", type: "string" },
          { field: "email_body", as: "followup_body", type: "string" },
          { field: "cta", as: "followup_cta", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "tone_appropriate", as: "tone_check", type: "string" },
          {
            field: "follow_up_timing_days",
            as: "followup_timing_days",
            type: "number",
          },
        ],
      ),

      // 4. Risk and tone validation (condition)
      n(
        "4",
        "condition",
        "Safe to send? (risk = low, tone = appropriate)",
        760,
        {
          conditionData: [
            {
              variable: "risk_level",
              value: "low",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Approval gate for strategic deals
      n(
        "5",
        "approvalGate",
        "Manager approval: strategic deal follow-up",
        1000,
        {
          approvalGateData: {
            message:
              "Review follow-up for {deal_name} (${deal_amount}).\n\nStage: {deal_stage}\nRisk: {risk_level}\nTo: {contact_first_name} {contact_last_name}\n\nSubject: {followup_subject}\nBody: {followup_body}\nCTA: {followup_cta}\n\nApprove to send, reject to revise.",
          },
        },
        460,
      ),

      // 6. Send follow-up email
      emailNode(
        "6",
        "Send stage-specific follow-up",
        1240,
        "{contact_email}",
        "{followup_subject}",
        "<p>{followup_body}</p><p>{followup_cta}</p>",
      ),

      // 7. Engagement monitor (delay + condition)
      n("7", "delayAction", "Wait for engagement signal", 1480, {
        delayAction: { time: 3, timeFormat: "days" },
      }),

      // 8. CRM forecast update
      n("8", "hitApi", "CRM update: forecast + activity log", 1720, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"notes_last_updated":"{current_timestamp}","last_follow_up_date":"{current_date}","follow_up_stage":"{deal_stage}","follow_up_risk":"{risk_level}","next_follow_up_days":"{followup_timing_days}"}',
            },
          ],
          variables: [
            { name: "id", customName: "deal_updated_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"), // low risk -> send directly
      e("4", "5", "output_2"), // higher risk -> manager approval
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Elite Automated Sales Follow-Up** — Stage-aware AI follow-ups with risk gating and CRM sync.\n\n1. Deal stage change in CRM triggers the workflow\n2. **Full deal context** assembled — amount, stage, contacts, timeline\n3. **AI writes stage-specific follow-up** (demo recap, proposal reinforcement, negotiation urgency, or onboarding prep)\n4. **Risk + tone validation** gates the email\n5. Low risk → sends automatically; higher risk → **manager approval gate**\n6. Follow-up email sent to primary contact\n7. **3-day engagement wait** for response tracking\n8. **CRM forecast update** logs activity, risk level, and next follow-up timing\n\n**To activate:** Say "connect hubspot", "connect openai", "connect email"`,
  },

  // ===================================================================
  // 5. MEETING FOLLOW-UP AUTOMATOR
  // ===================================================================

  "Elite Meeting Follow-Up Automator": {
    nodes: [
      trigger("Calendar: Meeting completed", "webhook_app", {
        source: "Calendly / Google Calendar / Zoom",
        event: "meeting.ended",
        howItStarts:
          "A sales meeting ends (via calendar event completion or Zoom meeting ended webhook)",
        setup:
          "Connect Calendly -> Developer -> Webhooks -> Event: invitee.created + meeting.ended, or Zoom -> Marketplace -> Webhook -> meeting.ended. Point to your workflow URL.",
        inputFields: [],
      }),

      // 2. Entity matching to account/deal
      n("2", "hitApi", "Match meeting to CRM account + deal", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{attendee_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","company","associatedcompanyid","hubspot_owner_id"]',
            },
            { key: "associations", value: '["deals"]' },
          ],
          variables: [
            { name: "results[0].id", customName: "contact_id", type: "string" },
            {
              name: "results[0].properties.firstname",
              customName: "contact_first_name",
              type: "string",
            },
            {
              name: "results[0].properties.lastname",
              customName: "contact_last_name",
              type: "string",
            },
            {
              name: "results[0].properties.company",
              customName: "company_name",
              type: "string",
            },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "deal_owner_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI decision + action extraction
      aiNode(
        "3",
        "AI extract decisions + action items",
        520,
        "You are a meeting follow-up assistant. From the meeting context, extract key decisions made, action items for each party, next steps, and any blockers or concerns raised. Categorize each action item by owner (us vs them) and priority. Respond JSON: {decisions: string, our_action_items: string, their_action_items: string, next_steps: string, blockers: string, meeting_sentiment: string, urgency: string}",
        "Meeting: {meeting_title}, Duration: {meeting_duration} min, Attendees: {attendee_names}, Host: {host_name}, Notes/Transcript: {meeting_notes}",
        [
          { field: "decisions", as: "decisions", type: "string" },
          { field: "our_action_items", as: "our_action_items", type: "string" },
          {
            field: "their_action_items",
            as: "their_action_items",
            type: "string",
          },
          { field: "next_steps", as: "next_steps", type: "string" },
          { field: "blockers", as: "blockers", type: "string" },
          {
            field: "meeting_sentiment",
            as: "meeting_sentiment",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 4. External follow-up draft
      aiNode(
        "4",
        "AI draft external follow-up email",
        760,
        "Write a professional meeting follow-up email to the prospect. Include: thank them for their time, summarize key decisions, list their action items, confirm next steps, and propose a timeline. Keep under 200 words. Respond JSON: {subject: string, email_body: string}",
        "To: {contact_first_name} {contact_last_name} at {company_name}, Decisions: {decisions}, Their actions: {their_action_items}, Next steps: {next_steps}, Sentiment: {meeting_sentiment}",
        [
          { field: "subject", as: "external_subject", type: "string" },
          { field: "email_body", as: "external_email_body", type: "string" },
        ],
      ),

      // 5. Internal CRM notes draft
      aiNode(
        "5",
        "AI draft internal CRM notes",
        760,
        "Write concise internal CRM notes for this meeting. Format: DECISIONS (bullet points), OUR ACTION ITEMS (with owners and deadlines), THEIR ACTION ITEMS, BLOCKERS, SENTIMENT ASSESSMENT, NEXT MEETING TARGET. Keep factual and scannable. Respond JSON: {crm_notes: string, deal_health: string, recommended_stage: string}",
        "Meeting: {meeting_title}, Company: {company_name}, Decisions: {decisions}, Our actions: {our_action_items}, Their actions: {their_action_items}, Blockers: {blockers}, Sentiment: {meeting_sentiment}",
        [
          { field: "crm_notes", as: "crm_notes", type: "string" },
          { field: "deal_health", as: "deal_health", type: "string" },
          {
            field: "recommended_stage",
            as: "recommended_stage",
            type: "string",
          },
        ],
        460,
      ),

      // 6. Host approval gate
      n("6", "approvalGate", "Host reviews follow-up + notes", 1000, {
        approvalGateData: {
          message:
            "Review meeting follow-up for {meeting_title} with {contact_first_name} {contact_last_name} ({company_name}).\n\nExternal email subject: {external_subject}\nSentiment: {meeting_sentiment}\nDeal health: {deal_health}\n\nApprove to send follow-up and update CRM.",
        },
      }),

      // 7. Send email + create CRM tasks
      emailNode(
        "7",
        "Send external follow-up email",
        1240,
        "{attendee_email}",
        "{external_subject}",
        "<p>{external_email_body}</p>",
      ),

      // 8. System updates + account team notify
      slackNode(
        "8",
        "Notify account team + update CRM",
        1480,
        "📋 *Meeting Follow-Up Sent* — {meeting_title}\n\nCompany: {company_name}\nSentiment: {meeting_sentiment} | Deal Health: {deal_health}\nDecisions: {decisions}\nOur Actions: {our_action_items}\nNext Steps: {next_steps}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Elite Meeting Follow-Up Automator** — AI-powered post-meeting workflow with dual-track drafting.\n\n1. Meeting completion triggers via Calendly/Zoom webhook\n2. **CRM entity match** links attendee to contact, company, and deal\n3. **AI extracts decisions + action items** categorized by owner, with sentiment analysis\n4. **Two parallel AI drafts**: external follow-up email + internal CRM notes\n5. **Host approval gate** reviews both before sending\n6. Approved → follow-up email sent to prospect\n7. **Slack notification** to account team with decisions, actions, and deal health\n\n**To activate:** Say "connect calendly" (or "connect zoom"), "connect hubspot", "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 6. MEETING BOOKED TO CRM
  // ===================================================================

  "Elite Meeting Booked to CRM": {
    nodes: [
      trigger("Calendly: Meeting booked", "webhook_app", {
        source: "Calendly",
        event: "invitee.created",
        howItStarts:
          "A prospect books a meeting via your Calendly scheduling link",
        setup:
          "In Calendly -> Integrations -> Webhooks -> Subscribe to invitee.created event -> paste your workflow URL. All booking data (name, email, answers, event type) will be sent automatically.",
        inputFields: [],
      }),

      // 2. Identity resolution + dedupe
      n("2", "hitApi", "Identity resolution: CRM dedupe", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{invitee_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","company","lifecyclestage","hubspot_owner_id","num_associated_deals"]',
            },
          ],
          variables: [
            { name: "total", customName: "existing_contacts", type: "number" },
            {
              name: "results[0].id",
              customName: "existing_contact_id",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "current_stage",
              type: "string",
            },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "current_owner",
              type: "string",
            },
          ],
        },
      }),

      // 3. Pre-meeting enrichment via Clearbit
      n("3", "hitApi", "Pre-meeting enrich via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={invitee_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            {
              name: "person.employment.title",
              customName: "job_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "company.description",
              customName: "company_description",
              type: "string",
            },
            { name: "person.bio", customName: "person_bio", type: "string" },
          ],
        },
      }),

      // 4. Qualification pre-check (AI scoring)
      aiNode(
        "4",
        "AI pre-meeting qualification",
        760,
        "Pre-qualify this meeting booking. Score the prospect 1-100 based on: title/seniority (decision-maker?), company size, industry fit, revenue. Also determine meeting priority (high/medium/low) and suggest 3 key talking points for the AE. Respond JSON: {qualification_score: number, meeting_priority: string, is_decision_maker: string, talking_points: string, company_summary: string, risk_flags: string}",
        "Name: {invitee_name}, Email: {invitee_email}, Title: {job_title}, Seniority: {seniority}, Company: {company_name}, Employees: {employee_count}, Revenue: {annual_revenue}, Industry: {industry}, Bio: {person_bio}, Meeting Type: {event_type_name}, Existing in CRM: {existing_contacts}, Current Stage: {current_stage}",
        [
          { field: "qualification_score", as: "qual_score", type: "number" },
          { field: "meeting_priority", as: "meeting_priority", type: "string" },
          {
            field: "is_decision_maker",
            as: "is_decision_maker",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "company_summary", as: "company_summary", type: "string" },
          { field: "risk_flags", as: "risk_flags", type: "string" },
        ],
      ),

      // 5. CRM contact/deal creation
      n("5", "hitApi", "Create/update CRM contact + deal", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{invitee_email}","firstname":"{invitee_first_name}","lastname":"{invitee_last_name}","jobtitle":"{job_title}","company":"{company_name}","industry":"{industry}","qualification_score":"{qual_score}","meeting_priority":"{meeting_priority}","lifecyclestage":"salesqualifiedlead"}',
            },
          ],
          variables: [{ name: "id", customName: "contact_id", type: "string" }],
        },
      }),

      // 6. Owner notification + prep brief (Slack)
      slackNode(
        "6",
        "AE prep brief via Slack",
        1240,
        "📅 *New Meeting Booked* — {meeting_priority} priority\n\n*{invitee_name}* ({invitee_email})\n{job_title} at {company_name} ({employee_count} emp, {industry})\nDecision Maker: {is_decision_maker}\nQual Score: {qual_score}/100\n\n*Company:* {company_summary}\n\n*Talking Points:*\n{talking_points}\n\n*Risk Flags:* {risk_flags}\n\nMeeting: {event_type_name} on {event_start_time}",
      ),

      // 7. Prospect confirmation email
      emailNode(
        "7",
        "Send prospect confirmation + prep",
        1240,
        "{invitee_email}",
        "Looking forward to our meeting, {invitee_first_name}!",
        "<p>Hi {invitee_first_name},</p><p>Thanks for booking time with us! Just confirming our <strong>{event_type_name}</strong> on <strong>{event_start_time}</strong>.</p><p>To make the most of our time together, here is what we will cover:</p><ul><li>Understanding {company_name}'s current challenges</li><li>How teams in {industry} are solving similar problems</li><li>Next steps if there is a fit</li></ul><p>If you need to reschedule, you can do so via the calendar invite.</p><p>See you soon!</p>",
        460,
      ),

      // 8. Attendance tracking writeback
      n("8", "hitApi", "CRM writeback: meeting booked + enriched", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/meetings",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_meeting_title":"{event_type_name}","hs_meeting_start_time":"{event_start_time}","hs_meeting_end_time":"{event_end_time}","hs_meeting_outcome":"SCHEDULED","hs_internal_meeting_notes":"Qual Score: {qual_score}, Priority: {meeting_priority}, Talking Points: {talking_points}"}',
            },
            {
              key: "associations",
              value:
                '[{"to":{"id":"{contact_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":200}]}]',
            },
          ],
          variables: [{ name: "id", customName: "meeting_id", type: "string" }],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary: `**Elite Meeting Booked to CRM** — Automatic enrichment, qualification, AE prep, and CRM sync when a prospect books.\n\n1. Calendly booking triggers the workflow\n2. **CRM dedupe** checks for existing contacts and lifecycle stage\n3. **Clearbit enrichment** pulls title, seniority, company size, revenue, industry\n4. **AI pre-qualifies** the booking — scores prospect, determines priority, generates talking points\n5. **CRM contact created/updated** with enriched data and qualification score\n6. **Slack prep brief** sent to AE with company summary, talking points, and risk flags\n7. **Confirmation email** sent to prospect with agenda preview\n8. **CRM meeting record** created with internal notes and associations\n\n**To activate:** Say "connect calendly", "connect hubspot", "connect clearbit", "connect openai", "connect slack", "connect email"`,
  },

  // ===================================================================
  // 7. DEAL STAGNATION ALERT
  // ===================================================================

  "Elite Deal Stagnation Alert": {
    nodes: [
      trigger("Schedule: Daily pipeline health scan", "schedule", {
        schedule: "Every weekday at 8:00 AM",
        howItStarts:
          "Runs automatically every weekday morning to scan the pipeline for stagnating deals",
        setup:
          "Activate this workflow — it runs on schedule. Connect HubSpot for CRM access. Set your Slack channel for alerts.",
        inputFields: [
          { name: "stagnation_threshold_days", type: "number", example: "14" },
          { name: "pipeline_id", type: "string", example: "default" },
        ],
      }),

      // 2. Context enrichment from CRM
      n("2", "hitApi", "Pull pipeline deals from CRM", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"pipeline","operator":"EQ","value":"{pipeline_id}"},{"propertyName":"dealstage","operator":"NOT_IN","values":["closedwon","closedlost"]}]}]',
            },
            {
              key: "properties",
              value:
                '["dealname","amount","dealstage","closedate","hubspot_owner_id","notes_last_updated","hs_lastmodifieddate","days_to_close","hs_deal_stage_probability"]',
            },
            {
              key: "sorts",
              value:
                '[{"propertyName":"hs_lastmodifieddate","direction":"ASCENDING"}]',
            },
            { key: "limit", value: "50" },
          ],
          variables: [
            { name: "total", customName: "total_open_deals", type: "number" },
            { name: "results", customName: "pipeline_deals", type: "array" },
          ],
        },
      }),

      // 3. AI stagnation classification
      aiNode(
        "3",
        "AI classify stagnation risk",
        520,
        "Analyze these pipeline deals and identify stagnating ones. A deal is stagnating if: no activity for {stagnation_threshold_days}+ days, close date has passed, or probability has not changed in 2+ weeks. For each stagnating deal, classify the risk: CRITICAL (close date passed + no activity), HIGH (no activity 14+ days), MEDIUM (slowing momentum). Respond JSON: {stagnating_deals: string, critical_count: number, high_count: number, medium_count: number, total_stagnating: number, total_at_risk_revenue: string, pipeline_health_grade: string}",
        "Pipeline deals: {pipeline_deals}, Stagnation threshold: {stagnation_threshold_days} days, Today: {current_date}",
        [
          { field: "stagnating_deals", as: "stagnating_deals", type: "string" },
          { field: "critical_count", as: "critical_count", type: "number" },
          { field: "high_count", as: "high_count", type: "number" },
          { field: "medium_count", as: "medium_count", type: "number" },
          { field: "total_stagnating", as: "total_stagnating", type: "number" },
          {
            field: "total_at_risk_revenue",
            as: "at_risk_revenue",
            type: "string",
          },
          {
            field: "pipeline_health_grade",
            as: "health_grade",
            type: "string",
          },
        ],
      ),

      // 4. AI next-best-action recommendation
      aiNode(
        "4",
        "AI recommend next-best-actions",
        760,
        "For each stagnating deal, recommend a specific next-best-action. Options: RE-ENGAGE (personalized check-in), ESCALATE (involve leadership), DISCOUNT (time-limited offer), REQUALIFY (confirm still in market), CLOSE_LOST (recommend archiving). Be specific about what to say/do. Respond JSON: {action_cards: string, highest_priority_deal: string, highest_priority_action: string, summary_recommendation: string}",
        "Stagnating deals: {stagnating_deals}, Pipeline health: {health_grade}, Critical: {critical_count}, At-risk revenue: {at_risk_revenue}",
        [
          { field: "action_cards", as: "action_cards", type: "string" },
          {
            field: "highest_priority_deal",
            as: "top_priority_deal",
            type: "string",
          },
          {
            field: "highest_priority_action",
            as: "top_priority_action",
            type: "string",
          },
          {
            field: "summary_recommendation",
            as: "summary_recommendation",
            type: "string",
          },
        ],
      ),

      // 5. Rep alert card (Slack)
      slackNode(
        "5",
        "Rep Slack alert: stagnating deals",
        1000,
        "⚠️ *Daily Pipeline Health Report* — Grade: {health_grade}\n\n*{total_stagnating} deals stagnating* | Revenue at risk: {at_risk_revenue}\nCritical: {critical_count} | High: {high_count} | Medium: {medium_count}\n\n*Top Priority:* {top_priority_deal}\nAction: {top_priority_action}\n\n{summary_recommendation}",
      ),

      // 6. Manager digest email
      emailNode(
        "6",
        "Manager digest: pipeline health",
        1000,
        "{manager_email}",
        "Pipeline Health Report — {health_grade} | {at_risk_revenue} at risk",
        "<h2>Daily Pipeline Health — Grade: {health_grade}</h2><p><strong>{total_stagnating} deals stagnating</strong> out of {total_open_deals} open deals</p><table><tr><th>Risk</th><th>Count</th></tr><tr><td>Critical</td><td>{critical_count}</td></tr><tr><td>High</td><td>{high_count}</td></tr><tr><td>Medium</td><td>{medium_count}</td></tr></table><h3>Revenue at Risk: {at_risk_revenue}</h3><h3>Action Cards</h3><p>{action_cards}</p><h3>Summary</h3><p>{summary_recommendation}</p>",
        460,
      ),

      // 7. Task + due date creation
      n("7", "hitApi", "Create CRM tasks for top deals", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_task_subject":"Stagnation Action: {top_priority_deal}","hs_task_body":"{top_priority_action}","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hs_timestamp":"{current_timestamp}"}',
            },
          ],
          variables: [{ name: "id", customName: "task_id", type: "string" }],
        },
      }),

      // 8. Outcome tracking writeback
      n("8", "hitApi", "Log pipeline scan to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/PipelineHealth:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{current_date}","{health_grade}","{total_open_deals}","{total_stagnating}","{critical_count}","{high_count}","{medium_count}","{at_risk_revenue}","{summary_recommendation}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("5", "7"),
      e("7", "8"),
    ],
    summary: `**Elite Deal Stagnation Alert** — Daily AI-powered pipeline health monitoring with actionable next-best-actions.\n\n1. Runs every weekday at 8 AM — scans all open deals\n2. **CRM pipeline pull** retrieves deals with activity timestamps and stage data\n3. **AI classifies stagnation** — critical (overdue + silent), high (14+ days idle), medium (slowing)\n4. **AI recommends specific actions** per deal — re-engage, escalate, discount, requalify, or close-lost\n5. **Slack alert** to reps with top-priority deal and action card\n6. **Manager email digest** with full pipeline health report, risk breakdown, and revenue at risk\n7. **CRM task created** for highest-priority stagnating deal\n8. **Google Sheets log** tracks pipeline health trends over time\n\n**To activate:** Say "connect hubspot", "connect openai", "connect slack", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // 8. DEAL WON CELEBRATION
  // ===================================================================

  "Elite Deal Won Celebration": {
    nodes: [
      trigger("CRM: Deal closed-won", "webhook_app", {
        source: "Salesforce / HubSpot",
        event: "deal.propertyChange",
        howItStarts: "A deal moves to 'Closed Won' stage in your CRM pipeline",
        setup:
          "In HubSpot -> Workflows -> Deal-based -> Trigger: Deal stage = Closed Won, or Salesforce -> Process Builder -> Opportunity Stage = Closed Won. Point webhook to your workflow URL.",
        inputFields: [],
      }),

      // 2. Commercial validation (condition: contract signed?)
      n("2", "hitApi", "Fetch deal details + validate completeness", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?associations=contacts,companies&properties=dealname,amount,dealstage,closedate,hubspot_owner_id,hs_acv,contract_signed,payment_terms,deal_currency_code",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            {
              name: "properties.dealname",
              customName: "deal_name",
              type: "string",
            },
            {
              name: "properties.amount",
              customName: "deal_amount",
              type: "string",
            },
            {
              name: "properties.hs_acv",
              customName: "annual_contract_value",
              type: "string",
            },
            {
              name: "properties.closedate",
              customName: "close_date",
              type: "string",
            },
            {
              name: "properties.contract_signed",
              customName: "contract_signed",
              type: "string",
            },
            {
              name: "properties.hubspot_owner_id",
              customName: "deal_owner_id",
              type: "string",
            },
            {
              name: "properties.payment_terms",
              customName: "payment_terms",
              type: "string",
            },
            {
              name: "associations.contacts.results[0].id",
              customName: "primary_contact_id",
              type: "string",
            },
            {
              name: "associations.companies.results[0].id",
              customName: "company_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. Condition: contract signed?
      n("3", "condition", "Contract signed and validated?", 520, {
        conditionData: [
          {
            variable: "contract_signed",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Internal Slack announcement (true path)
      slackNode(
        "4",
        "Slack: Deal won celebration",
        760,
        "🎉🎉🎉 *DEAL WON!* 🎉🎉🎉\n\n*{deal_name}* — ${deal_amount}\nACV: ${annual_contract_value}\nOwner: {deal_owner_name}\nClose Date: {close_date}\nPayment Terms: {payment_terms}\n\nCongratulations to the team! 🏆",
      ),

      // 5. Commission + revenue sync (Google Sheets)
      n("5", "hitApi", "Log to revenue tracking sheet", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{revenue_spreadsheet_id}/values/ClosedWon:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{close_date}","{deal_name}","{deal_amount}","{annual_contract_value}","{deal_owner_name}","{payment_terms}","{company_name}","{primary_contact_name}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "revenue_rows_added",
              type: "number",
            },
          ],
        },
      }),

      // 6. Customer handoff launch (webhook to onboarding)
      n("6", "hitApi", "Launch customer onboarding workflow", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://your-onboarding-workflow.omniengage.com/webhook/onboarding-intake",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Authorization", value: "Bearer {internal_webhook_key}" },
          ],
          body: [
            { key: "deal_id", value: "{deal_id}" },
            { key: "deal_name", value: "{deal_name}" },
            { key: "deal_amount", value: "{deal_amount}" },
            { key: "contact_id", value: "{primary_contact_id}" },
            { key: "contact_email", value: "{contact_email}" },
            { key: "contact_name", value: "{primary_contact_name}" },
            { key: "company_id", value: "{company_id}" },
            { key: "company_name", value: "{company_name}" },
            { key: "payment_terms", value: "{payment_terms}" },
            { key: "close_date", value: "{close_date}" },
          ],
          variables: [
            {
              name: "status",
              customName: "onboarding_trigger_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Advocacy capture prompt email
      emailNode(
        "7",
        "Send advocacy + referral prompt",
        1480,
        "{contact_email}",
        "Welcome aboard, {primary_contact_name}! Quick question...",
        "<p>Hi {primary_contact_name},</p><p>We are thrilled to have {company_name} on board!</p><p>As we get your onboarding started, I wanted to ask: <strong>would you be open to sharing your experience?</strong></p><ul><li>A brief quote we can use (takes 2 minutes)</li><li>A short case study (we handle the writing)</li><li>A referral to someone who might benefit similarly</li></ul><p>No pressure at all — we just find that our best partnerships start with mutual enthusiasm.</p><p>Your onboarding team will be in touch shortly with next steps.</p><p>Thanks again for choosing us!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // contract signed -> celebrate
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Elite Deal Won Celebration** — End-to-end closed-won automation: validation, celebration, revenue tracking, onboarding, and advocacy.\n\n1. Deal moves to Closed Won in CRM\n2. **Deal details fetched** — amount, ACV, contract status, payment terms, associations\n3. **Validation gate** confirms contract is signed before proceeding\n4. **Slack celebration** announces the win with full deal details\n5. **Google Sheets revenue log** tracks closed deals for commission and reporting\n6. **Onboarding webhook** launches customer success workflow with deal context\n7. **Advocacy email** sent to champion asking for testimonial, case study, or referral\n\n**To activate:** Say "connect hubspot", "connect slack", "connect google sheets", "connect email"`,
  },
};
